import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { Button } from "@/shared/ui/button";

import { useUsers } from "../api/use-users";
import { DeleteUserDialog } from "../components/DeleteUserDialog";
import { ResetPasswordDialog } from "../components/ResetPasswordDialog";
import { SetRolesDialog } from "../components/SetRolesDialog";
import { UserFormModal } from "../components/UserFormModal";
import { UserTable } from "../components/UserTable";

type AdminUser = components["schemas"]["AdminUser"];
type Action = { type: "edit" | "roles" | "reset" | "delete"; user: AdminUser } | null;

const PAGE_LIMIT = 25;

interface UsersPageProps {
  /** Whether the current user holds `users:manage` — gates create/edit/roles/reset/delete. */
  canManage: boolean;
}

export function UsersPage({ canManage }: UsersPageProps): JSX.Element {
  const { t } = useTranslation(["users", "nav"]);
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError } = useUsers({ offset, limit: PAGE_LIMIT });
  const [createOpen, setCreateOpen] = useState(false);
  const [action, setAction] = useState<Action>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? items.length;

  return (
    <div data-testid="users-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.admin")}</span>
            <span className="text-muted-foreground">/</span>
            <span>{t("page.title")}</span>
          </>
        }
        title={t("page.title")}
        subtitle={t("page.subtitle")}
      />

      {canManage && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setCreateOpen(true)} data-testid="users-new">
            {t("action.new_user")}
          </Button>
        </div>
      )}

      <UserTable
        items={items}
        isLoading={isLoading}
        isError={isError}
        canManage={canManage}
        onEdit={(user) => setAction({ type: "edit", user })}
        onSetRoles={(user) => setAction({ type: "roles", user })}
        onResetPassword={(user) => setAction({ type: "reset", user })}
        onDelete={(user) => setAction({ type: "delete", user })}
      />

      {!isLoading && !isError && (
        <Pagination
          total={total}
          offset={offset}
          limit={PAGE_LIMIT}
          onChange={({ offset: next }) => setOffset(next)}
        />
      )}

      {createOpen && <UserFormModal mode="create" onClose={() => setCreateOpen(false)} />}

      {action?.type === "edit" && (
        <UserFormModal
          mode="edit"
          editTarget={{
            id: action.user.id,
            email: action.user.email,
            display_name: action.user.display_name,
            roles: action.user.roles,
          }}
          onClose={() => setAction(null)}
        />
      )}
      {action?.type === "roles" && (
        <SetRolesDialog user={action.user} onClose={() => setAction(null)} />
      )}
      {action?.type === "reset" && (
        <ResetPasswordDialog user={action.user} onClose={() => setAction(null)} />
      )}
      {action?.type === "delete" && (
        <DeleteUserDialog user={action.user} onClose={() => setAction(null)} />
      )}
    </div>
  );
}
